/**
 * Chart data extraction utilities for DOM Observer Context
 * Simplified to focus on Chart.js (used via react-chartjs-2)
 */

/**
 * Internal type definitions for Chart.js and React internals
 */

// Chart.js instance structure (supports both v2 and v3+)
type ChartJsInstance = {
  config?: {
    data?: ChartJsData;
    options?: {
      plugins?: {
        title?: {
          text?: string;
        };
      };
      title?: {
        text?: string;
      };
    };
    type?: string;
  };
  // v2 stores these at top level (not nested under config)
  data?: ChartJsData;
  options?: {
    plugins?: {
      title?: {
        text?: string;
      };
    };
    title?: {
      text?: string;
    };
  };
  type?: string;
};

// Chart.js data structure
type ChartJsData = {
  datasets?: ChartJsDataset[];
  labels?: Array<number | string>;
};

// Chart.js dataset structure
type ChartJsDataset = {
  data?: Array<ChartJsDataValue | number>;
  label?: string;
};

// Chart.js data value (can be number or object with x/y)
type ChartJsDataValue = {
  label?: string;
  value?: number;
  x?: number | string;
  y?: number;
};

// Window with Chart.js global
type WindowWithChart = Window & {
  Chart?: {
    getChart?: (canvas: HTMLCanvasElement) => ChartJsInstance | undefined;
  };
};

// Canvas element with Chart.js properties
type CanvasWithChart = HTMLCanvasElement & {
  [key: string]: unknown;
  __chart__?: ChartJsInstance;
  chart?: ChartJsInstance; // For React Fiber keys
};

// React Fiber structure
type ReactFiber = {
  return?: ReactFiber;
  stateNode?: {
    chart_instance?: ChartJsInstance;
    chartInstance?: ChartJsInstance;
  };
};

/**
 * Exported types for chart data
 */

export type ChartDataPoint = {
  label?: string;
  value: number;
  x?: number | string;
  y?: number;
};

export type ChartDataset = {
  data: ChartDataPoint[];
  label?: string;
};

export type ChartTrend = 'decreasing' | 'increasing' | 'stable' | 'volatile';

export type ChartInfo = {
  chartId: string;
  chartType: 'area' | 'bar' | 'bubble' | 'doughnut' | 'line' | 'pie' | 'polarArea' | 'radar' | 'scatter' | 'unknown';
  datasets: ChartDataset[];
  labels?: Array<number | string>;
  summary: {
    dataPointCount: number;
    datasetCount: number;
    hasMultipleDatasets: boolean;
    maxValue?: number;
    minValue?: number;
    trend?: ChartTrend;
  };
  title?: string;
};

export type ChartsData = {
  chartCount: number;
  charts: ChartInfo[];
};

/**
 * Detect trend in a series of values
 */
function detectTrend(values: number[]): ChartTrend {
  if (values.length < 2) {
    return 'stable';
  }

  let increases = 0;
  let totalChange = 0;

  for (let i = 1; i < values.length; i += 1) {
    const change = values[i] - values[i - 1];
    totalChange += Math.abs(change);
    if (change > 0) {
      increases += 1;
    }
  }

  const avgValue = values.reduce((sum, val) => sum + val, 0) / values.length;
  const volatility = totalChange / (values.length - 1) / avgValue;

  // High volatility = volatile
  if (volatility > 0.2) {
    return 'volatile';
  }

  // Determine overall trend
  const trendRatio = increases / (values.length - 1);
  if (trendRatio > 0.6) {
    return 'increasing';
  }
  if (trendRatio < 0.4) {
    return 'decreasing';
  }
  return 'stable';
}

/**
 * Extract Chart.js data from canvas elements
 * Works with Chart.js v2 and v3+ (used by react-chartjs-2)
 */
function extractChartJsData(canvas: HTMLCanvasElement): ChartInfo | null {
  try {
    const typedCanvas = canvas as CanvasWithChart;
    const typedWindow = window as WindowWithChart;

    // Try Chart.js v3+ official API first
    let chartInstance: ChartJsInstance | undefined;
    if (typedWindow.Chart && typeof typedWindow.Chart.getChart === 'function') {
      chartInstance = typedWindow.Chart.getChart(canvas);
    }

    // Fallback to v2 properties or instance stored directly on canvas
    if (!chartInstance) {
      chartInstance = typedCanvas.chart || typedCanvas.__chart__;
    }

    // Try to access through React Fiber (react-chartjs-2 stores instance on component)
    if (!chartInstance) {
      const reactFiberKey = Object.keys(typedCanvas).find((key) => key.startsWith('__reactFiber$'));
      if (reactFiberKey) {
        const fiber = typedCanvas[reactFiberKey] as ReactFiber;
        // Navigate up the fiber tree to find the react-chartjs-2 component
        let currentFiber: ReactFiber | undefined = fiber;
        let attempts = 0;
        while (currentFiber && attempts < 10) {
          const instance = currentFiber.stateNode;
          if (instance) {
            // react-chartjs-2 stores the Chart.js instance as chartInstance on the component
            if (instance.chartInstance) {
              chartInstance = instance.chartInstance;
              break;
            }
            // Also check for chart_instance (underscore variant)
            if (instance.chart_instance) {
              chartInstance = instance.chart_instance;
              break;
            }
          }
          currentFiber = currentFiber.return;
          attempts += 1;
        }
      }
    }

    if (!chartInstance) {
      return null;
    }

    // Extract config and data (handle both v2 and v3+ structure)
    const config = chartInstance.config || chartInstance;
    const data = config?.data || chartInstance?.data;

    if (!data) {
      return null;
    }

    // Extract basic info
    const chartType = (config.type || 'unknown') as ChartInfo['chartType'];
    const title = config.options?.plugins?.title?.text || config.options?.title?.text;
    const labels = data.labels || [];

    // Extract datasets with data points
    const datasets: ChartDataset[] = (data.datasets || []).map((dataset: ChartJsDataset) => {
      const dataPoints: ChartDataPoint[] = (dataset.data || []).map(
        (value: ChartJsDataValue | number, index: number) => {
          // Handle different data formats: numbers, {x, y}, or {value, label}
          if (typeof value === 'number') {
            return {
              label: labels[index] as string | undefined,
              value,
            };
          }
          if (typeof value === 'object' && value !== null) {
            return {
              x: value.x,
              y: value.y,
              value: value.y || value.value || 0,
              label: value.label || (labels[index] as string | undefined),
            };
          }
          return { value: 0 };
        }
      );

      return {
        label: dataset.label,
        data: dataPoints,
      };
    });

    // Calculate summary statistics
    const allValues = datasets.flatMap((ds) => ds.data.map((d) => d.value));
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : undefined;
    const minValue = allValues.length > 0 ? Math.min(...allValues) : undefined;

    // Detect trend for single-dataset line/area charts
    let trend: ChartTrend | undefined;
    if ((chartType === 'line' || chartType === 'area') && datasets.length === 1) {
      trend = detectTrend(datasets[0].data.map((d) => d.value));
    }

    // Generate chart ID
    const chartId =
      canvas.id || canvas.getAttribute('data-testid') || `chart-${Math.random().toString(36).substr(2, 9)}`;

    return {
      chartId,
      chartType,
      title,
      labels,
      datasets,
      summary: {
        datasetCount: datasets.length,
        dataPointCount: allValues.length,
        hasMultipleDatasets: datasets.length > 1,
        maxValue,
        minValue,
        trend,
      },
    };
  } catch (error) {
    console.warn('[ChartScanner] Failed to extract Chart.js data:', error);
    return null;
  }
}

/**
 * Scan for all Chart.js charts on the page
 * Simplified to only scan canvas elements (no SVG or data-attribute scanning)
 */
export function scanCharts(): ChartsData {
  const charts: ChartInfo[] = [];

  // Scan all canvas elements for Chart.js instances
  const canvases = document.querySelectorAll('canvas');

  canvases.forEach((canvas) => {
    const chartData = extractChartJsData(canvas);
    if (chartData) {
      charts.push(chartData);
    }
  });

  console.debug('[ChartScanner] Scan complete:', {
    totalCharts: charts.length,
    chartTypes: charts.map((c) => c.chartType),
    chartIds: charts.map((c) => c.chartId),
  });

  return {
    chartCount: charts.length,
    charts,
  };
}
